<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Globe, ChevronDown, Check } from 'lucide-vue-next'
import { useSettingsStore } from '@/stores/settings'
import { useClickOutside } from '@/composables/useClickOutside'
import type { SupportedLocale } from '@/plugins/i18n'

const { locale } = useI18n()
const settingsStore = useSettingsStore()
const isOpen = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)

useClickOutside(wrapperRef, () => { isOpen.value = false })

const languages: { code: SupportedLocale; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'zh', name: '中文' },
  { code: 'hi', name: 'हिन्दी' },
]

function select(code: SupportedLocale) {
  locale.value = code
  settingsStore.setLanguage(code)
  isOpen.value = false
}
</script>

<template>
  <div ref="wrapperRef" class="relative">
    <button class="btn btn-icon" @click.stop="isOpen = !isOpen" aria-label="Change language">
      <Globe :size="16" />
    </button>
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="isOpen"
        class="absolute top-[calc(100%+8px)] right-0 min-w-[150px] bg-surface border border-border rounded-md shadow-lg z-[1000] overflow-hidden"
      >
        <button
          v-for="lang in languages"
          :key="lang.code"
          :class="[
            'flex items-center justify-between w-full px-4 py-2.5 bg-transparent border-none text-sm cursor-pointer transition-colors duration-150',
            settingsStore.language === lang.code
              ? 'text-brand font-medium'
              : 'text-foreground hover:bg-surface-tertiary',
          ]"
          @click="select(lang.code)"
        >
          <span>{{ lang.name }}</span>
          <Check v-if="settingsStore.language === lang.code" :size="14" />
        </button>
      </div>
    </Transition>
  </div>
</template>
